import {observable, action} from 'mobx';
import moment from 'moment';
import AuditLog from './models/AuditLog';
import Pagination from './Pagination';
import axiosInstance, {downloadFile} from '~/utils/axiosInstance';
import localeText from '@locale';
import uris from '~/uris';

interface IFilterList {
  userTypes: listType[];
  resourceTypes: listType[];
  eventNames: listType[];
}

type listType = {
  code: string;
  nameZh: string;
  nameEn: string;
}

const initFilterList: IFilterList = {
  userTypes: [],
  resourceTypes: [],
  eventNames: []
};

type filterType = {
  filed: string;
  value: string;
  isFuzzy: string;
}

interface IQueryBody {
  startTime?: number;
  endTime?: number;
  filter?: filterType[];
}

interface IBody {
  forceFresh?: boolean;
}

const initQueryBody: IQueryBody = {
  // startTime: moment().subtract(7, 'days').startOf('day').unix() * 1000,
  endTime: moment().endOf('day').unix() * 1000,
  filter: []
};

const supportType = 'application/vnd.ms-excel;charset=utf-8';

export class AuditLogStore extends Pagination {
  @observable isLoading = false;

  @observable filterList: IFilterList = initFilterList;

  @observable auditLog = [];

  @observable querybody: IQueryBody = initQueryBody;

  @observable exportLoading = false;

  @observable logDuration;

  @observable startTime: number;

  @observable endTime: number;

  @action
  setLogDuration(data: number): void {
    this.logDuration = data;
  }

  @action
  setExportLoading(boolean): void {
    this.exportLoading = boolean;
  }

  @action.bound
  resetLoading(): void {
    this.isLoading = false;
  }

  @action.bound
  setFilterList(data): void {
    this.filterList = data;
  }

  @action.bound
  setAuditLog(data): void {
    this.auditLog = data;
  }

  @action.bound
  setSelectedTime(startTime, endTime): void {
    this.startTime = startTime;
    this.endTime = endTime;
  }

  @action.bound
  changePagination(pagination): void {
    super.changePagination(pagination);
  }

  @action.bound
  queryAuditLog(body = {}): void {
    this.isLoading = true;
    axiosInstance
      .post(
        uris.queryAuditLog(), {
          ...body
        })
      .then((data: any) => {
        this.setPagination(data.pagination);
        this.setAuditLog((data.auditLog || []).map(json => this.updateAuditLogFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  queryAuditLogWithAddition(querybody?: IQueryBody, body?: IBody): any {
    this.querybody = {
      ...this.querybody, ...querybody
    };

    let data = {
      ...this.paginationRequestBody
    };

    if (body && body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.queryAuditLog({
      ...data,
      ...body,
      ...this.querybody
    });
  }

  @action.bound
  getFilterAuditList(): Promise<void> {
    this.isLoading = true;
    return axiosInstance
      .get(
        uris.getFilterAuditList()
      )
      .then((data) => {
        this.setFilterList(data);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  // 下载文件，输入data，和命名的文件名
  downloadFile(res, filename): void {
    const blob = new Blob([res], {type: supportType});
    if (window.navigator.msSaveBlob) { // IE
      window.navigator.msSaveOrOpenBlob(blob, `${filename}`);
    }
    const fileURL = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = fileURL;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(fileURL);
  }

  // @action.bound
  // exportAuditLog(): Promise<void> {
  //   this.setExportLoading(true);
  //   return downloadFile(uris.exportAuditLog())
  //     .then((response) => {
  //       this.downloadFile(response, `${localeText('AUDIT.TITLE_EXPORT_AUDIT_LOG',
  //         moment().subtract(this.logDuration, 'days').format('YYYY.MM.DD'),
  //         moment().format('YYYY.MM.DD'))}`);
  //       this.setExportLoading(false);
  //     })
  //     .catch(() => {
  //       this.setExportLoading(false);
  //     });
  // }

  // 按筛选时间导出审计记录
  @action.bound
  exportAuditLog(query = {}): Promise<void> {
    this.setExportLoading(true);
    return downloadFile(uris.exportAuditLog(),
      {
        params: {
          ...query
        }
      }
    )
      .then((response) => {
        this.downloadFile(response, `${localeText('AUDIT.TITLE_EXPORT_AUDIT_LOG',
          moment().format('YYYYMMDDHHmmss'))}`);
        this.setExportLoading(false);
      })
      .catch(() => {
        this.setExportLoading(false);
      });
  }

  @action.bound
  getLogDuration(): Promise<void> {
    return axiosInstance
      .get(uris.getLogDuration())
      .then((data: any) => {
        this.setLogDuration(data - 1);
      });
  }

  @action('下载导入记录文件')
  downloadRecord(id): Promise<void> {
    return downloadFile(uris.downloadFailedRecord(), {
      params: {
        recordId: id
      }
    })
      .then((response) => {
        this.downloadFile(response, `${localeText('USER.TITLE_HISTORY_RECORDS_NAME', new Date().toISOString().substring(0, 10))}`);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  @action.bound
  updateAuditLogFromServer(json): any {
    let auditLog = this.auditLog.find(({eventTime}) => eventTime === json.eventTime);
    if (!auditLog) {
      auditLog = new AuditLog();
    }
    auditLog.updateFromJson(json);
    return auditLog;
  }
}

export default new AuditLogStore();
